import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Divider,
  Alert,
  Button,
  Stack,
  Paper,
  CircularProgress,
  Unstable_Grid2 as Grid,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import logoSAVERH from "../../../../assets/images/logoSAVERH.svg";
import TaskHeader from "../../../../components/TaskHeader";
import TaskGrid from "../../../../components/TaskGrid";
import TypeToggleButton from "../../../../components/TypeToggleButton";
import StartDateDialog from "../../../../components/StartDateDialog";

import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useOrganization } from "..";
import { useAuth } from "../../../../contexts/AuthContext";
import { useDialog } from "../../../../contexts/DialogContext";

import { fetchData } from "../../../../fetchData";

type FormValues = {
  type: string;
};

type Schedule = {
  frequency: "daily" | "weekly" | "monthly";
  dayOfWeek: number[];
  monthsInterval?: number;
  startDate?: string;
};

type SaveRHTask = {
  on: boolean;
  name: string;
  date: string;
  responsible: string | number;
  schedule?: Schedule;
};

const SaveRHTab = () => {
  const auth = useAuth();
  const snackbar = useSnackbar();
  const { data: organization, set } = useOrganization();
  const theme = useTheme();
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [progress, setProgress] = useState(false);
  const [tasks, setTasks] = useState<SaveRHTask[]>([]);
  const [tasksBackup, setTasksBackup] = useState<SaveRHTask[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [taskErrors, setTaskErrors] = useState<{ responsible: boolean }[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<any[]>([]);
  const [masterSwitch, setMasterSwitch] = useState(false);
  const [startDateDialogOpen, setStartDateDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const dialog = useDialog();

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      type: organization.products?.saveRH?.type || "one",
    },
  });

  async function loadData() {
    setProgress(true);
    try {
      const usersResult = await fetchData("/runrunit/users", {
        method: "GET",
      });
      setUsers(usersResult);

      const result = await fetchData(`/task-types?autoUpload=true`, {
        method: "GET",
      });
      const filtered = (result?.rows || []).filter(
        (t: any) => t.tribe && t.tribe.includes("saveRH"),
      );
      setFilteredTasks(filtered);

      const client = await fetchData(
        `/organizations/${organization.reference}/ref`,
        { method: "GET" },
      );

      reset({
        type: client.products?.saveRH?.type || "one",
      });

      const autoTasks = client?.autoTasks || [];
      const mapped = filtered.map((t: any) => {
        const found = autoTasks.find(
          (a: any) => a.taskTypeID === t.runrunit_id,
        );
        return {
          on: found ? found.active : false,
          name: t.name,
          date: found ? String(found.uploadDay).padStart(2, "0") : "01",
          responsible: found ? found.user : "",
          schedule: found?.schedule
            ? {
                frequency: found.schedule.frequency || "monthly",
                dayOfWeek: found.schedule.dayOfWeek || [],
                monthsInterval: found.schedule.monthsInterval || 1,
                startDate: found.schedule.startDate
                  ? new Date(found.schedule.startDate).toLocaleDateString(
                      "pt-BR",
                    )
                  : undefined,
              }
            : {
                frequency: "monthly",
                dayOfWeek: [],
                monthsInterval: 1,
              },
        } as SaveRHTask;
      });
      setTasks(mapped);
      setTasksBackup(mapped);
    } catch (e) {
      console.error("Erro no loadData SAVE RH:", e);
      setTasks([]);
      setTasksBackup([]);
      setUsers([]);
    }
    setProgress(false);
  }

  const handleMasterSwitchChange = (checked: boolean) => {
    const currentTasks = [...tasks];
    const updatedTasks = currentTasks.map((task) => ({ ...task, on: checked }));
    setTasks(updatedTasks);
    setMasterSwitch(checked);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [organization.reference]);

  async function onSubmit(data: any) {
    // Validação das tasks
    const errors = tasks.map((task) => ({
      responsible: task.on && !task.responsible,
    }));
    setTaskErrors(errors);
    const hasError = errors.some((err) => err.responsible);
    if (hasError) {
      return;
    }

    // Verificar se há tarefas ativas com intervalo >= 2 meses
    const tasksWithInterval = tasks.filter(
      (task) =>
        task.on &&
        task.schedule?.frequency === "monthly" &&
        (task.schedule?.monthsInterval || 1) >= 2,
    );

    if (tasksWithInterval.length > 0) {
      setPendingFormData(data);
      setStartDateDialogOpen(true);
      return;
    }

    await saveWithConflictCheck(data, false);
  }

  async function saveWithConflictCheck(formData: any, forceUpdate = false) {
    setSaving(true);

    const autoTasks = tasks.map((task) => {
      const taskType = filteredTasks.find((ft) => ft.name === task.name);
      return {
        taskTypeID: (taskType && taskType.runrunit_id) || null,
        taskName: (taskType && taskType.name) || "",
        active: task.on,
        uploadDay: Number(task.date),
        user: task.responsible,
        tribe: (taskType && taskType.tribe) || [],
        schedule: task.schedule,
      };
    });

    const payload = {
      ...organization,
      products: {
        ...organization.products,
        saveRH: {
          ...organization.products?.saveRH,
          type: formData.type,
        },
      },
      autoTasks,
      forceUpdate,
    };

    try {
      await fetchData(`/organizations/${organization._id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      set(payload);
      setIsEditing(false);
      setTasksBackup(tasks);
      snackbar?.open("Tarefas do SAVE RH atualizadas com sucesso!", "success");
      await loadData();
    } catch (error: any) {
      const errorMessage = error?.message || "";
      if (errorMessage.startsWith("TASK_CONFLICTS:")) {
        const conflictsJson = errorMessage.replace("TASK_CONFLICTS:", "");
        try {
          const conflicts = JSON.parse(conflictsJson);
          dialog?.open(
            "Conflito de Tasks",
            <Box sx={{ p: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                As seguintes tasks não podem estar ativas ao mesmo tempo:
              </Alert>

              {conflicts.map((conflict: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: 1,
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Conflito {index + 1}:
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label="Nova Task" color="primary" size="small" />
                      <Typography variant="body2">
                        {conflict.newTask.name} (Dia{" "}
                        {conflict.newTask.uploadDay} - {conflict.newTask.user})
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label="Task Conflitante"
                        color="error"
                        size="small"
                      />
                      <Typography variant="body2">
                        {conflict.conflictingTask.name} (Dia{" "}
                        {conflict.conflictingTask.uploadDay} -{" "}
                        {conflict.conflictingTask.user})
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Alert severity="info" sx={{ mt: 2 }}>
                Se você prosseguir, as tasks conflitantes serão automaticamente
                desativadas.
              </Alert>

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button onClick={dialog?.close} color="inherit">
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    dialog?.close();
                    saveWithConflictCheck(formData, true);
                  }}
                  variant="contained"
                  color="warning"
                >
                  Prosseguir e Desativar Conflitantes
                </Button>
              </Stack>
            </Box>,
          );
          setSaving(false);
          return;
        } catch (e) {
          console.error("Erro ao parsear conflitos:", e);
        }
      }

      snackbar?.open(
        "Ops! No momento não foi possível realizar a ação.",
        "error",
      );
    }
    setSaving(false);
  }

  const handleStartDateDialogClose = () => {
    setStartDateDialogOpen(false);
    setPendingFormData(null);
  };

  const handleStartDateDialogConfirm = async (tasksWithStartDate: any[]) => {
    // Atualizar as tarefas com as datas de início configuradas
    const updatedTasks = tasks.map((task) => {
      const taskType = filteredTasks.find((ft) => ft.name === task.name);
      const taskTypeID = (taskType && taskType.runrunit_id) || 0;
      const updatedTask = tasksWithStartDate.find(
        (t) => t.taskTypeID === taskTypeID,
      );
      if (updatedTask && updatedTask.schedule) {
        return {
          ...task,
          schedule: updatedTask.schedule,
        };
      }
      return task;
    });

    setTasks(updatedTasks);
    setStartDateDialogOpen(false);

    // Agora salvar com os dados pendentes
    if (pendingFormData) {
      await saveWithConflictCheck(pendingFormData, false);
      setPendingFormData(null);
    }
  };

  // Converter tarefas para o formato esperado pelo dialog
  const tasksForDialog = tasks.map((task) => {
    const taskType = filteredTasks.find((ft) => ft.name === task.name);
    return {
      taskTypeID: (taskType && taskType.runrunit_id) || 0,
      taskName: (taskType && taskType.name) || task.name,
      active: task.on,
      uploadDay: Number(task.date),
      user: task.responsible,
      tribe: (taskType && taskType.tribe) || [],
      schedule: task.schedule,
    };
  });

  return (
    <Box sx={{ p: 2, position: "relative", pb: 4 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? "secondary.dark" : "primary.main",
            p: 1,
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <img
                src={logoSAVERH}
                alt="SAVE RH"
                style={{ height: 40, width: "auto" }}
              />
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mb: 0 }}
                color="primary.contrastText"
              >
                SAVE RH
              </Typography>
            </Box>
            <Typography variant="body2" color="primary.contrastText">
              Gestão completa de recursos humanos e departamento pessoal.
            </Typography>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          <Grid xs={12} lg={4}>
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">Tipo de produto</Typography>
                <Tooltip
                  title={
                    <Box>
                      <Box sx={{ mb: 1, fontWeight: "bold" }}>
                        • One: Ferramenta + Serviço (Completo)
                      </Box>
                      <Box sx={{ fontWeight: "bold" }}>• Core: Ferramenta</Box>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    sx={{
                      color: "primary.main",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Controller
                render={({ field }) => (
                  <TypeToggleButton
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isEditing}
                    options={{
                      left: { value: "one", label: "One" },
                      right: { value: "core", label: "Core" },
                    }}
                  />
                )}
                control={control}
                name="type"
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Box>
          <TaskHeader
            activeTasksCount={tasks.filter((t) => t.on).length}
            totalTasksCount={tasks.length}
            masterSwitch={masterSwitch}
            onMasterSwitchChange={handleMasterSwitchChange}
            isEditing={isEditing}
          />
          <TaskGrid
            tasks={tasks}
            setTasks={setTasks}
            users={users}
            isEditing={isEditing}
            progress={progress}
            taskErrors={taskErrors}
            setTaskErrors={setTaskErrors}
          />
        </Box>
      </form>

      {/* Botão flutuante fixo */}
      {auth?.user.permission_level === "manager" && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            {!isEditing && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
              </Button>
            )}
            {isEditing && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={async () => {
                    setIsEditing(false);
                    setTasks(tasksBackup);
                    await loadData();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onSubmit)}
                >
                  {!saving && "Salvar"}
                  {saving && (
                    <CircularProgress size={12} sx={{ color: "#FFF" }} />
                  )}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      )}

      <StartDateDialog
        open={startDateDialogOpen}
        onClose={handleStartDateDialogClose}
        onConfirm={handleStartDateDialogConfirm}
        tasks={tasksForDialog}
        loading={saving}
      />
    </Box>
  );
};

export default SaveRHTab;
